import type {
	AccessEventDetail,
	GuestAccessGrantDetail,
	PropertyContactsSummary,
	PropertyContactWithDetails,
	SharingUser,
} from '@/client/types.gen';
import { getExpiryLabel } from '@/utils/userDisplayUtils';
import { Roles, UserStatuses } from '@/utils/roleUtils';
import {
	accessEndTitle,
	AccessEvents,
	getClonedOwnershipTransferTimes,
	getGrantActivityAt,
	GrantActivityKinds,
	grantToSharingUser,
	GrantStatuses,
	isDeniedGrant,
	isExpiredGrant,
	isRevokedGrant,
	type AccessTransferRecord,
	type GrantActivityItem,
} from '@/utils/access/grantHelpers';

export type PastAccessItem =
	| { kind: typeof GrantStatuses.Expired; user: SharingUser; at: string; grant?: GuestAccessGrantDetail }
	| { kind: typeof GrantStatuses.Denied; user: SharingUser; at: string; grant: GuestAccessGrantDetail }
	| { kind: typeof GrantStatuses.Revoked; user: SharingUser; at: string; grant?: GuestAccessGrantDetail };

export const getPastAccessItemState = (item: PastAccessItem) => ({
	denied: item.kind === GrantStatuses.Denied,
	revoked: item.kind === GrantStatuses.Revoked,
	expired: item.kind === GrantStatuses.Expired,
});

/**
 * The backend now derives an "expired" status (e.g. a lapsed invite after a transfer), which is
 * authoritative even when expires_at is absent. Fall back to the timestamp for older records.
 */
export const isUserExpired = (user: SharingUser) =>
	user.status === UserStatuses.Expired || getExpiryLabel(user.expires_at).expired;

/**
 * Whether the access drawer's footer has a button to draw. The one case it has none is an Owner
 * the viewer cannot transfer from, and the panel draws a footer bar for any element it is handed.
 */
export const hasAccessDrawerFooterActions = (
	user: SharingUser,
	isPastAccess: boolean,
	canTransferOwnership: boolean,
): boolean => isPastAccess || user.role !== Roles.Owner || canTransferOwnership;

/**
 * Blocks granting to the owner's address. Every assignable role is below Owner, and an add writes
 * its role and expiry onto whatever association it matches, so a grant here would demote the owner
 * or lock them out of their own property. Matched on role rather than expiry so the state cannot be
 * re-entered from here.
 */
export const getOwnerEmailError = (email: string, members: SharingUser[]): string | null => {
	const normalized = email.trim().toLowerCase();
	if (!normalized) return null;
	const isOwner = members.some((member) => member.role === Roles.Owner && member.email.toLowerCase() === normalized);
	return isOwner ? 'This person owns the property' : null;
};

/**
 * Blocks inviting yourself, re-inviting an existing active member, or adding the property owner,
 * matched by email — the backend silently overwrites the matched association's role and expiry in
 * place with no self-check or already-has-access check, so this has to be caught client-side
 * before submission.
 */
export const getInviteEmailError = (
	email: string,
	currentUserEmail: string | null | undefined,
	activeMembers: SharingUser[],
): string | null => {
	const normalized = email.trim().toLowerCase();
	if (!normalized) return null;

	if (currentUserEmail && normalized === currentUserEmail.toLowerCase()) {
		return "You can't invite yourself";
	}

	const alreadyActive = activeMembers.some(
		(member) => !isUserExpired(member) && member.email.toLowerCase() === normalized,
	);
	if (alreadyActive) {
		return 'This person already has access';
	}

	// Expiry is what lets a lapsed guest be re-granted, so the check above ignores expired members
	// on purpose. The owner is the exception: an owner whose association has expired is locked out of
	// their own property while still being billed and emailed for it.
	return getOwnerEmailError(email, activeMembers);
};

const getTimestamp = (date?: string | null) => (date ? new Date(date).getTime() || 0 : 0);

const grantToPastAccessItem = (grant: GuestAccessGrantDetail): PastAccessItem[] => {
	if (isDeniedGrant(grant)) {
		return [{ kind: GrantStatuses.Denied, user: grantToSharingUser(grant), at: getGrantActivityAt(grant), grant }];
	}
	if (isRevokedGrant(grant)) {
		return [{ kind: GrantStatuses.Revoked, user: grantToSharingUser(grant), at: getGrantActivityAt(grant), grant }];
	}
	if (isExpiredGrant(grant)) {
		return [{ kind: GrantStatuses.Expired, user: grantToSharingUser(grant), at: getGrantActivityAt(grant), grant }];
	}
	return [];
};

const userToExpiredPastAccessItem = (user: SharingUser): PastAccessItem[] => {
	if (!isUserExpired(user) || !user.expires_at) return [];
	return [{ kind: GrantStatuses.Expired, user, at: user.expires_at }];
};

const indexLatestRevokedGrantByEmail = (grants: GuestAccessGrantDetail[]) => {
	const latestByEmail = new Map<string, GuestAccessGrantDetail>();
	grants.forEach((grant) => {
		const email = grant.requester?.email?.toLowerCase();
		if (!email || !isRevokedGrant(grant)) return;
		const current = latestByEmail.get(email);
		if (!current || getTimestamp(getGrantActivityAt(grant)) > getTimestamp(getGrantActivityAt(current))) {
			latestByEmail.set(email, grant);
		}
	});
	return latestByEmail;
};

const reconcileExpiredItemWithRevokedGrant = (
	item: PastAccessItem,
	latestRevokedGrantByEmail: ReadonlyMap<string, GuestAccessGrantDetail>,
	activityByUserId: ReadonlyMap<number, readonly AccessEventDetail[]>,
): PastAccessItem => {
	if (item.kind !== GrantStatuses.Expired || !item.user.email) return item;

	const email = item.user.email.toLowerCase();
	const accessCycleStartedAt = Math.max(
		getTimestamp(item.user.last_invited_at),
		getTimestamp(item.user.invite_accepted_at),
	);
	const revokedEvent = activityByUserId
		.get(item.user.user_id)
		?.filter((event) => event.event === AccessEvents.Revoked && getTimestamp(event.ts) >= accessCycleStartedAt)
		.sort((a, b) => getTimestamp(b.ts) - getTimestamp(a.ts))[0];

	// Sharing removals are flattened by the list API to status=expired/expires_at=now. The per-user
	// activity log preserves the actual reason access ended and is authoritative when available.
	if (revokedEvent) {
		return {
			kind: GrantStatuses.Revoked,
			user: item.user,
			at: revokedEvent.ts,
		};
	}

	const revokedGrant = latestRevokedGrantByEmail.get(email);

	if (!revokedGrant || getTimestamp(getGrantActivityAt(revokedGrant)) < accessCycleStartedAt) return item;
	return {
		kind: GrantStatuses.Revoked,
		user: item.user,
		at: getGrantActivityAt(revokedGrant),
		grant: revokedGrant,
	};
};

export const buildPastAccessItems = (
	users: SharingUser[] = [],
	grants: GuestAccessGrantDetail[] = [],
	activityByUserId: ReadonlyMap<number, readonly AccessEventDetail[]> = new Map(),
) => {
	// TODO: Include cancelled-but-never-accepted sharing invites here once the API returns a durable
	// cancelled invite/audit record. Today cancelling an invited SharingUser can remove it from the
	// sharing list entirely, so the frontend cannot reliably reconstruct it after refetch.
	const latestRevokedGrantByEmail = indexLatestRevokedGrantByEmail(grants);
	const expiredUserItems = users
		.flatMap(userToExpiredPastAccessItem)
		.map((item) => reconcileExpiredItemWithRevokedGrant(item, latestRevokedGrantByEmail, activityByUserId));

	// A guest-access grant duplicates a person the sharing list already accounts for once the
	// grant is renewed (the superseded grant lingers as expired/revoked) — the same person then
	// shows in both Current and History. The grant view also mislabels an invited user's timeline
	// as "Requested" instead of "Invited". Drop grant rows for anyone the sharing list represents
	// (a current user, or an already-emitted expired past row) so each person appears once, via
	// their sharing-list identity where one exists. Grants with no sharing match (genuine QR
	// requesters) are unaffected.
	const representedEmails = new Set(
		[...users.filter((user) => !isUserExpired(user)), ...expiredUserItems.map((item) => item.user)].flatMap(
			(user) => (user.email ? [user.email.toLowerCase()] : []),
		),
	);
	const grantItems = grants
		.flatMap(grantToPastAccessItem)
		.filter((item) => !representedEmails.has(item.user.email.toLowerCase()));

	return [...expiredUserItems, ...grantItems].sort((a, b) => getTimestamp(b.at) - getTimestamp(a.at));
};

export const getPastAccessItemKey = (item: PastAccessItem) => `${item.kind}-${item.user.user_id}`;

/** Guest-access grants tied to a person, matched by email (case-insensitive). */
export const grantsForEmail = (grants: GuestAccessGrantDetail[] = [], email: string | null | undefined) => {
	if (!email) return [];
	const target = email.toLowerCase();
	return grants.filter((grant) => grant.requester?.email?.toLowerCase() === target);
};

/**
 * Builds one chronological activity timeline for a single person, merging their sharing-list
 * identity (invite / accepted / expiry) with any guest-access grants tied to them. Collapsing
 * duplicate rows must not lose history, so a current user carries over the events from the
 * grant(s) that were superseded on renewal.
 *
 * The two access origins have distinct vocabularies: an owner invite reads "Invited" →
 * "Accepted invite", while a self-service QR request reads "Requested" → "Approved". So when the
 * person was owner-invited (the sharing user has `last_invited_at`), both the grant's "Requested"
 * and "Approved" events are suppressed — the owner's action is the "Invited" event, and surfacing
 * an "Approved" for an invite reads as if they requested and were granted access.
 */
export const buildAccessActivityItems = (
	user: SharingUser | null,
	grants: GuestAccessGrantDetail[] = [],
	options: { ownershipTransfers?: AccessTransferRecord[] } = {},
): GrantActivityItem[] => {
	const wasInvited = !!user?.last_invited_at;
	const events: GrantActivityItem[] = [];
	const ownershipTransferTimes = getClonedOwnershipTransferTimes(options.ownershipTransfers);

	// TODO(access-timeline): `last_invited_at` / `invite_accepted_at` are last-write-wins
	// timestamps, not discrete event records — extending or changing a user's access can bump
	// `last_invited_at`, so a mere expiry change surfaces as a fresh "Invited" event (and a
	// re-invite is indistinguishable from the original). A faithful timeline needs a backend audit
	// log of discrete events (invite sent, invite accepted, expiry changed) rather than these
	// mutable timestamps; until then these two events can misreport when they happened.
	if (user?.last_invited_at) {
		events.push({
			kind: GrantActivityKinds.Invited,
			title: 'Invited',
			date: user.last_invited_at,
		});
	}
	if (user?.invite_accepted_at) {
		events.push({
			kind: GrantActivityKinds.AcceptedInvite,
			title: 'Accepted invite',
			date: user.invite_accepted_at,
		});
	}

	grants.forEach((grant) => {
		// "Requested"/"Approved" are the self-service request vocabulary; an owner invite is
		// represented by the "Invited" event instead (see JSDoc).
		if (!wasInvited) {
			events.push({ kind: GrantActivityKinds.Requested, title: 'Requested', date: grant.created_at });
			if (grant.approved_at) {
				events.push({ kind: GrantActivityKinds.Approved, title: 'Approved', date: grant.approved_at });
			}
		}
		if (isDeniedGrant(grant)) {
			events.push({
				kind: GrantActivityKinds.Denied,
				title: 'Denied',
				date: grant.updated_at,
				fallback: 'Access request denied',
			});
		} else if (isRevokedGrant(grant)) {
			events.push({
				kind: GrantActivityKinds.Revoked,
				title:
					accessEndTitle(GrantActivityKinds.Revoked, grant.updated_at, { ownershipTransferTimes }) ??
					'Access removed',
				date: grant.updated_at,
				fallback: 'Access removed',
			});
		} else if (isExpiredGrant(grant)) {
			// TODO(access-timeline): a grant's `expires_at` can be a superseded expiry — when access
			// was extended or renewed before that date passed, the expiry never actually happened, yet
			// it still renders as an "Expired" event. The frontend cannot tell a genuinely-lapsed grant
			// from a superseded one; the backend needs to distinguish "expired" from "superseded by
			// renewal" (or omit superseded grants) before this event can be trusted.
			const date = grant.expires_at ?? grant.updated_at;
			events.push({
				kind: GrantActivityKinds.Expired,
				title: accessEndTitle(GrantActivityKinds.Expired, date, { ownershipTransferTimes }) ?? 'Access expired',
				date,
				fallback: 'Access expired',
			});
		}
	});

	// Only an expiry that has come is an event. One still ahead is a setting, and the drawer's
	// duration control is where it is read and changed.
	if (user?.expires_at && new Date(user.expires_at).getTime() < Date.now()) {
		events.push({
			kind: GrantActivityKinds.Expired,
			title:
				accessEndTitle(GrantActivityKinds.Expired, user.expires_at, { ownershipTransferTimes }) ??
				'Access expired',
			date: user.expires_at,
			fallback: 'Access expired',
		});
	}

	const seen = new Set<string>();
	return events
		.filter((event) => {
			const dedupeKey = `${event.kind}-${event.date ?? ''}`;
			if (seen.has(dedupeKey)) return false;
			seen.add(dedupeKey);
			return true;
		})
		.sort((a, b) => getTimestamp(b.date) - getTimestamp(a.date));
};

export const getContactEmailSet = (data: PropertyContactsSummary | undefined): Set<string> => {
	const contacts = data?.contacts_by_category.flatMap((group) => group.contacts) ?? [];
	return new Set(contacts.flatMap((contact) => (contact.contact.email ? [contact.contact.email.toLowerCase()] : [])));
};

/**
 * Narrows a list to the entries whose person a search term names.
 *
 * Name and address both, because half of any access list is people who have never signed in and
 * so are known only by the address they were invited at. An empty term is not a filter: it leaves
 * the list whole rather than emptying it.
 *
 * `toUser` reads the person out of each entry, so a list of access records filters on who each one
 * is about without being rebuilt into a list of people first. The term is normalised once here
 * rather than per entry.
 */
export const filterUsersBySearch = <T>(
	items: T[],
	search: string,
	toUser: (item: T) => Pick<SharingUser, 'name' | 'email'>,
): T[] => {
	const term = search.trim().toLowerCase();
	if (!term) return items;

	return items.filter((item) => {
		const user = toUser(item);
		return user.name?.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
	});
};

export interface InviteContactSuggestion {
	/** The card's name, as the household reads it. */
	label: string;
	/** Unique per card, not per address: two cards at one office inbox are one suggestion. */
	value: string;
	/** The address the invite would go to, shown because that is what the field is collecting. */
	detail: string;
}

/**
 * Contacts the property holds who cannot open it, as suggestions for the invite field.
 *
 * Offered so inviting the plumber already on file is a tap rather than a spelling exercise. Each
 * one is put through the same rule the field validates with, so nothing is ever suggested that
 * would be refused on the spot: not the reader, not a current member, not the owner.
 *
 * Historical cards are left out. They are a record of who used to do the work, and inviting a
 * former tradesperson onto the property is not a shortcut anybody is reaching for. A card with no
 * address is left out because there is nowhere to send the invite.
 */
export const getUninvitedContactSuggestions = (
	data: PropertyContactsSummary | undefined,
	currentUserEmail: string | null | undefined,
	activeMembers: SharingUser[],
): InviteContactSuggestion[] => {
	const contacts = data?.contacts_by_category.flatMap((group) => group.contacts) ?? [];
	const seen = new Set<string>();
	const suggestions: InviteContactSuggestion[] = [];

	for (const contact of contacts) {
		if (contact.is_connected || contact.historical === true) continue;

		const email = (contact.override_email ?? contact.contact.email)?.trim();
		if (!email) continue;

		const normalized = email.toLowerCase();
		if (seen.has(normalized)) continue;
		if (getInviteEmailError(email, currentUserEmail, activeMembers)) continue;

		seen.add(normalized);
		suggestions.push({
			label: contact.override_name ?? contact.contact.name,
			value: `contact:${contact.id}`,
			detail: email,
		});
	}

	return suggestions;
};

/**
 * The contact card a shared user is, if they are one.
 *
 * By the account a card names, because the card's own address need not be it: a company connected
 * at an office inbox puts the access on a card whose email is the company's, and matching on the
 * address missed them. A live card first — a card keeps naming its account after a disconnect, so
 * a disconnected one answers only when no connected card does. The address is the last fallback,
 * for somebody who was made a contact from their address rather than connected from a card.
 */
export const findContactForUser = (
	data: PropertyContactsSummary | undefined,
	user: SharingUser,
): PropertyContactWithDetails | undefined => {
	if (!data) return undefined;
	const contacts = data.contacts_by_category.flatMap((group) => group.contacts);

	return (
		contacts.find((contact) => contact.is_connected && contact.account?.user_id === user.user_id) ??
		contacts.find((contact) => contact.account?.user_id === user.user_id) ??
		findContactByEmail(data, user.email)
	);
};

/** The card that holds this address, by the address it is reached at: an override wins over the contact's own. */
export const findContactByEmail = (
	data: PropertyContactsSummary | undefined,
	email: string,
	/** A card to leave out, where the question is whether any other card holds the address. */
	exceptId?: number,
): PropertyContactWithDetails | undefined => {
	const wanted = email.trim().toLowerCase();
	if (!data || !wanted) return undefined;
	return data.contacts_by_category
		.flatMap((group) => group.contacts)
		.find(
			(contact) =>
				contact.id !== exceptId &&
				(contact.override_email ?? contact.contact.email)?.trim().toLowerCase() === wanted,
		);
};
